import { useEffect } from 'react';
import {
  type QueryConfig,
  type QueryFunction,
  type QueryKey,
  type QueryResult,
  type TypedQueryFunction,
  type TypedQueryFunctionArgs,
  useQuery as _useQuery,
} from 'react-query';

import { mockRequest } from '/debug/mocks';
import { mockSettingsStorage } from '/debug/mocks/storage';
import type { QueryMockConfig } from '/debug/mocks/types';

export function useQuery<TResult, TError, TArgs extends TypedQueryFunctionArgs>(
  queryKey: readonly QueryKey[],
  queryFn: TypedQueryFunction<TResult, TArgs>,
  queryConfig?: QueryConfig<TResult, TError> & { mock?: QueryMockConfig },
): QueryResult<TResult, TError>;
export function useQuery<TResult = unknown, TError = unknown>(
  queryKey: readonly QueryKey[],
  queryFn: QueryFunction<TResult>,
  queryConfig?: QueryConfig<TResult, TError> & { mock?: QueryMockConfig },
): QueryResult<TResult, TError>;
export function useQuery<TResult, TError>(
  queryKey: readonly QueryKey[],
  // biome-ignore lint/suspicious/noExplicitAny: need any for TS to properly infer
  queryFn: any,
  // biome-ignore lint/suspicious/noExplicitAny: need any for TS to properly infer
  queryConfig: any,
): QueryResult<TResult, TError> {
  const mockConf = queryConfig?.mock;

  // Automatically refetch the query when the mock setting changes
  const mockSetting = mockSettingsStorage.useValue(mockConf);

  const query: QueryResult<TResult, TError> = _useQuery(queryKey, mockRequest(queryFn, mockConf), queryConfig);

  // biome-ignore lint/correctness/useExhaustiveDependencies(mockSetting): trigger
  // biome-ignore lint/correctness/useExhaustiveDependencies(queryConfig?.enabled): stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(query.refetch): stable
  useEffect(() => {
    if (queryConfig?.enabled) {
      query.refetch();
    }
  }, [mockSetting]);

  return query;
}
